import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getStewardship,
  setStewardshipEnabled,
  getUserProfile,
  updateUserProfile,
  getUserFamiliesDetailed,
  getPendingInvitations,
  setPrimaryFamily,
  createNewFamily,
  joinFamilyWithCode,
  getFamilyMembers,
  uploadProfilePhoto,
} from '../services/api';
import HubLayout from '../components/common/HubLayout';
import StewardDialog from '../components/settings/StewardDialog';
import {
  Settings,
  Lock,
  MessageSquare,
  Share2,
  Bell,
  Clock,
  Shield,
  Eye,
  Users,
  User,
  Heart,
  Send,
  Plus,
  ChevronRight,
  Save,
  RotateCcw,
  Home,
  Camera,
  MapPin,
  Calendar,
  Mail,
  Phone,
  ExternalLink,
  Check,
} from 'lucide-react';
import { getUser } from '../utils/secureStorage';
import '../styles/settings.css';

/* ─── Selfie Modal ─── */
function SelfieModal({ isOpen, onClose, onCapture }) {
  const videoRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const [ready, setReady] = React.useState(false);
  const [captured, setCaptured] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!isOpen) return;
    setReady(false);
    setCaptured(null);
    setError(null);

    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 640 } }, audio: false })
      .then(stream => {
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => { videoRef.current.play(); setReady(true); };
        }
      })
      .catch(() => setError('Could not access camera. Please check your browser permissions.'));

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
  }, [isOpen]);

  const snap = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const size = Math.min(video.videoWidth, video.videoHeight);
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    // Center-crop to square and mirror horizontally
    const sx = (video.videoWidth - size) / 2;
    const sy = (video.videoHeight - size) / 2;
    ctx.save();
    ctx.translate(size, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size);
    ctx.restore();
    canvas.toBlob(blob => {
      if (blob) setCaptured({ blob, url: URL.createObjectURL(blob) });
    }, 'image/jpeg', 0.92);
  };

  const retake = () => {
    if (captured?.url) URL.revokeObjectURL(captured.url);
    setCaptured(null);
  };

  const confirm = () => {
    if (captured) {
      const file = new File([captured.blob], `selfie-${Date.now()}.jpg`, { type: 'image/jpeg' });
      onCapture(file);
    }
    onClose();
  };

  const handleClose = () => {
    if (captured?.url) URL.revokeObjectURL(captured.url);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div onClick={handleClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(18,20,14,.85)', zIndex: 2000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 20, maxWidth: 420, width: '100%',
        boxShadow: '0 30px 80px rgba(24,28,16,.4)', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #F0EEE3' }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#2C2F26', fontFamily: "'Lora', Georgia, serif" }}>Take a selfie</h3>
          <button onClick={handleClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <span style={{ fontSize: 22, color: '#828A76' }}>&times;</span>
          </button>
        </div>

        {/* Camera / Preview */}
        <div style={{ position: 'relative', background: '#1a1a1a', aspectRatio: '1/1' }}>
          {error ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 32, textAlign: 'center' }}>
              <p style={{ color: '#fff', fontSize: 15 }}>{error}</p>
            </div>
          ) : captured ? (
            <img src={captured.url} alt="Selfie preview" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          ) : (
            <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transform: 'scaleX(-1)' }} />
          )}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          {!ready && !error && !captured && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 36, height: 36, border: '3px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            </div>
          )}
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '18px 20px' }}>
          {error ? (
            <button onClick={handleClose} style={{ background: '#556B2F', color: '#fff', fontWeight: 700, fontSize: 15, padding: '12px 28px', borderRadius: 999, border: 'none', cursor: 'pointer' }}>
              Close
            </button>
          ) : captured ? (
            <>
              <button onClick={retake} style={{ background: '#fff', color: '#556B2F', fontWeight: 700, fontSize: 15, padding: '12px 24px', borderRadius: 999, border: '1.5px solid #C4CBB4', cursor: 'pointer' }}>
                Retake
              </button>
              <button onClick={confirm} style={{ background: '#556B2F', color: '#fff', fontWeight: 700, fontSize: 15, padding: '12px 28px', borderRadius: 999, border: 'none', cursor: 'pointer' }}>
                Use this photo
              </button>
            </>
          ) : (
            <button onClick={snap} disabled={!ready} style={{
              width: 68, height: 68, borderRadius: '50%', border: '4px solid #556B2F',
              background: ready ? '#fff' : '#ddd', cursor: ready ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'transform .1s',
            }}
              onMouseDown={e => { if (ready) e.currentTarget.style.transform = 'scale(0.9)'; }}
              onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <div style={{ width: 54, height: 54, borderRadius: '50%', background: '#556B2F' }} />
            </button>
          )}
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/**
 * SettingsPage - Redesigned per mockup
 *
 * Sections: Privacy, Interactions, Sharing, Notifications,
 * Time Capsules, Stewardship with sticky save bar.
 */
function SettingsPage() {
  const navigate = useNavigate();

  // Privacy
  const [storyVisibility, setStoryVisibility] = useState('family');
  const [profileVisibility, setProfileVisibility] = useState('family');

  // Interactions
  const [allowComments, setAllowComments] = useState(true);
  const [allowReactions, setAllowReactions] = useState(true);
  const [showActivityStatus, setShowActivityStatus] = useState(true);
  const [allowFamilyQuestions, setAllowFamilyQuestions] = useState(true);

  // Sharing
  const [shareWithExtendedFamily, setShareWithExtendedFamily] = useState(false);
  const [allowLinkSharing, setAllowLinkSharing] = useState(false);

  // Notifications
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);

  // Time Capsules
  const [timeCapsuleEnabled, setTimeCapsuleEnabled] = useState(true);
  const [autoDelivery, setAutoDelivery] = useState(true);
  const [deliveryNotifications, setDeliveryNotifications] = useState(true);
  const [capsuleReminders, setCapsuleReminders] = useState(true);
  const [allowCapsuleReplies, setAllowCapsuleReplies] = useState(true);

  // Stewardship
  const [enableStewardship, setEnableStewardship] = useState(true);
  const [stewardshipSaving, setStewardshipSaving] = useState(false);
  const [stewardshipError, setStewardshipError] = useState('');

  // Stewardship API data
  const [stewardship, setStewardship] = useState(null);
  const [loadingStewardship, setLoadingStewardship] = useState(true);
  const [stewardRelationships, setStewardRelationships] = useState({});
  const [stewardDialog, setStewardDialog] = useState(null); // { mode, designation }
  const [stewardNotice, setStewardNotice] = useState('');

  // Personal Information
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [showPhotoMenu, setShowPhotoMenu] = useState(false);
  const [showSelfieModal, setShowSelfieModal] = useState(false);
  const fileInputRef = useRef(null);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editNickname, setEditNickname] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editDob, setEditDob] = useState('');
  const [editLocation, setEditLocation] = useState('');

  // My Families
  const [families, setFamilies] = useState([]);
  const [familiesLoading, setFamiliesLoading] = useState(true);
  const [pendingInvitations, setPendingInvitations] = useState([]);

  const handlePhotoUpload = async (file) => {
    if (!file || photoUploading) return;
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`Photo is too large (${(file.size / (1024*1024)).toFixed(1)} MB). Maximum size is 10 MB.`);
      return;
    }

    // Show the photo immediately in the avatar
    const previewUrl = URL.createObjectURL(file);
    setAvatarUrl(previewUrl);
    setPhotoUploading(true);

    try {
      const familyId = localStorage.getItem('familyId');
      const userData = getUser();
      if (!familyId) {
        alert('No family selected. Please select a family first.');
        return;
      }
      // The members API returns `id` = Person UUID (not FamilyMember PK)
      // uploadProfilePhoto expects the Person UUID as memberId
      const myPersonId = userData?.person_id;
      if (myPersonId) {
        await uploadProfilePhoto(familyId, myPersonId, file);
      } else {
        alert('Could not determine your profile. Photo was not saved.');
      }
    } catch (err) {
      console.error('Photo upload error:', err);
      alert(err.message || 'Failed to upload photo. Please try again.');
    } finally {
      setPhotoUploading(false);
      setShowPhotoMenu(false);
    }
  };

  useEffect(() => {
    const fetchStewardship = async () => {
      try {
        const data = await getStewardship();
        setStewardship(data);
        setEnableStewardship(data.enabled);
      } catch (error) {
        console.error('Failed to fetch stewardship data:', error);
        setStewardshipError('Could not load your stewardship settings.');
      } finally {
        setLoadingStewardship(false);
      }

      // Relationship labels are display-only; the list works without them
      try {
        const familyId = localStorage.getItem('familyId');
        if (familyId) {
          const membersData = await getFamilyMembers(familyId);
          const byId = {};
          (membersData.members || []).forEach((m) => {
            byId[m.id] = m.relationship || '';
          });
          setStewardRelationships(byId);
        }
      } catch (error) {
        console.error('Failed to fetch family members for stewardship:', error);
      }
    };
    fetchStewardship();

    // Fetch profile
    const fetchProfile = async () => {
      try {
        const data = await getUserProfile();
        const p = data.profile;
        setProfile(p);
        setEditFirstName(p.first_name || '');
        setEditLastName(p.last_name || '');
        setEditNickname(p.nickname || '');
        setEditPhone(p.phone || '');
        setEditDob(p.date_of_birth || '');
        setEditLocation(p.location || '');
        if (p.profile_photo_url) {
          const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
          const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '');
          setAvatarUrl(p.profile_photo_url.startsWith('http') ? p.profile_photo_url : `${API_ORIGIN}${p.profile_photo_url}`);
        }
        if (p.default_story_visibility) {
          setStoryVisibility(p.default_story_visibility);
        }
      } catch (error) {
        console.error('Failed to fetch profile:', error);
      } finally {
        setProfileLoading(false);
      }
    };
    fetchProfile();

    // Fetch families
    const fetchFamilies = async () => {
      try {
        const data = await getUserFamiliesDetailed();
        setFamilies(data.families || []);
      } catch (error) {
        console.error('Failed to fetch families:', error);
      } finally {
        setFamiliesLoading(false);
      }
    };
    fetchFamilies();

    // Fetch pending invitations
    const fetchInvitations = async () => {
      try {
        const data = await getPendingInvitations();
        setPendingInvitations(data.invitations || []);
      } catch (error) {
        console.error('Failed to fetch invitations:', error);
      }
    };
    fetchInvitations();
  }, []);

  const handleUpdateProfile = async () => {
    setProfileSaving(true);
    try {
      await updateUserProfile({
        first_name: editFirstName,
        last_name: editLastName,
        nickname: editNickname,
        phone: editPhone,
        date_of_birth: editDob || null,
        location: editLocation,
      });
      // Re-fetch profile to confirm
      const data = await getUserProfile();
      setProfile(data.profile);
    } catch (error) {
      console.error('Failed to update profile:', error);
    } finally {
      setProfileSaving(false);
    }
  };

  const handleSetPrimary = async (familyId) => {
    try {
      await setPrimaryFamily(familyId);
      const data = await getUserFamiliesDetailed();
      setFamilies(data.families || []);
    } catch (error) {
      console.error('Failed to set primary family:', error);
    }
  };

  const handleCreateFamily = async () => {
    const name = prompt('Enter a name for your new family:');
    if (!name) return;
    try {
      await createNewFamily(name);
      const data = await getUserFamiliesDetailed();
      setFamilies(data.families || []);
    } catch (error) {
      console.error('Failed to create family:', error);
      alert(error.message || 'Failed to create family');
    }
  };

  const handleJoinFamily = async () => {
    const code = prompt('Enter the family invite code or share link:');
    if (!code) return;
    try {
      await joinFamilyWithCode(code);
      const data = await getUserFamiliesDetailed();
      setFamilies(data.families || []);
    } catch (error) {
      console.error('Failed to join family:', error);
      alert(error.message || 'Failed to join family');
    }
  };

  const familyColors = ['#556B2F', '#8B4513', '#2E4057', '#6B3FA0', '#C2185B', '#00695C'];

  // Toggle switch component matching mockup
  const Toggle = ({ enabled, onChange }) => (
    <div
      className={`stg-toggle ${enabled ? 'on' : ''}`}
      onClick={() => onChange(!enabled)}
    >
      <div className="stg-toggle-knob" />
    </div>
  );

  const handleStewardshipToggle = async (next) => {
    if (stewardshipSaving) return;
    const withSteward = (stewardship?.total_letters || 0) - (stewardship?.letters_without_steward || 0);
    if (!next && withSteward > 0) {
      const ok = window.confirm(
        `${withSteward} of your letters already ${withSteward === 1 ? 'has' : 'have'} a steward. ` +
        'They will keep them. Only new letters will be affected. Turn off stewardship?'
      );
      if (!ok) return;
    }
    const previous = enableStewardship;
    setEnableStewardship(next);
    setStewardshipSaving(true);
    setStewardshipError('');
    try {
      const data = await setStewardshipEnabled(next);
      setStewardship(data);
      setEnableStewardship(data.enabled);
    } catch (error) {
      console.error('Failed to save stewardship setting:', error);
      setEnableStewardship(previous);
      setStewardshipError('Could not save this change. Please try again.');
    } finally {
      setStewardshipSaving(false);
    }
  };

  const hasPrimary = !!stewardship?.designations?.some((d) => d.role === 'PRIMARY');
  const hasBackup = !!stewardship?.designations?.some((d) => d.role === 'BACKUP');

  const handleStewardDone = (data) => {
    const n = data.letters_updated || 0;
    setStewardship(data);
    setStewardDialog(null);
    setStewardNotice(n > 0 ? `Updated the steward on ${n} ${n === 1 ? 'letter' : 'letters'}.` : '');
  };

  // Toggle row component
  const ToggleRow = ({ title, subtitle, enabled, onChange }) => (
    <div className="stg-toggle-row">
      <div className="stg-toggle-info">
        <div className="stg-toggle-title">{title}</div>
        <div className="stg-toggle-sub">{subtitle}</div>
      </div>
      <Toggle enabled={enabled} onChange={onChange} />
    </div>
  );

  return (
    <>
    <HubLayout activeView="settings">
      <div className="stg-container">

        {/* Page Header */}
        <div className="stg-page-header">
          <div className="stg-page-header-icon">
            <Settings size={24} />
          </div>
          <h1>Settings</h1>
        </div>
        <p className="stg-page-subtitle">
          Manage your privacy, sharing preferences, and time capsule settings.
        </p>

        {/* ═══ PERSONAL INFORMATION ═══ */}
        <div className="stg-section" id="personal-info">
          <div className="stg-section-header">
            <User size={20} />
            <h2>
              <span className="stg-new-badge">NEW</span>
              Personal Information
            </h2>
          </div>
          <p className="stg-section-sub">
            Your account details shared across all your families.
          </p>

          <div className="stg-card">
            {profileLoading ? (
              <div className="stg-loading">
                <div className="stg-loading-spinner" />
                <span>Loading profile...</span>
              </div>
            ) : (
              <>
                {/* Avatar + Name/Email Row */}
                <div className="stg-profile-row">
                  <div className="stg-avatar-wrapper" style={{ position: 'relative' }}>
                    <div className="stg-avatar" style={{ background: profile?.avatar_color || '#556B2F', overflow: 'hidden' }}>
                      {avatarUrl ? (
                        <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                      ) : (
                        profile?.avatar_initial || editFirstName?.charAt(0)?.toUpperCase() || 'U'
                      )}
                    </div>
                    <div className="stg-avatar-edit" onClick={() => setShowPhotoMenu(!showPhotoMenu)} style={{ cursor: 'pointer' }}>
                      {photoUploading ? <div style={{ width: 14, height: 14, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} /> : <Camera size={14} />}
                    </div>

                    {/* Photo menu dropdown */}
                    {showPhotoMenu && (
                      <>
                        <div onClick={() => setShowPhotoMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 14 }} />
                        <div style={{
                          position: 'absolute', top: '100%', left: 0, marginTop: 6,
                          background: '#fff', border: '1px solid #E4E1D2', borderRadius: 12,
                          boxShadow: '0 8px 24px rgba(40,46,28,.15)', zIndex: 15, overflow: 'hidden', minWidth: 180,
                        }}>
                          <button onClick={() => {
                            setShowPhotoMenu(false);
                            setShowSelfieModal(true);
                          }} style={{
                            width: '100%', padding: '12px 16px', background: 'none', border: 'none', borderBottom: '1px solid #F0EEE3',
                            textAlign: 'left', cursor: 'pointer', fontSize: 15, color: '#2C2F26', fontFamily: "'Source Sans 3', sans-serif",
                            display: 'flex', alignItems: 'center', gap: 10,
                          }}>
                            <Camera size={16} color="#556B2F" /> Take a selfie
                          </button>
                          <button onClick={() => { setShowPhotoMenu(false); fileInputRef.current?.click(); }} style={{
                            width: '100%', padding: '12px 16px', background: 'none', border: 'none',
                            textAlign: 'left', cursor: 'pointer', fontSize: 15, color: '#2C2F26', fontFamily: "'Source Sans 3', sans-serif",
                            display: 'flex', alignItems: 'center', gap: 10,
                          }}>
                            <ExternalLink size={16} color="#556B2F" /> Upload a photo
                          </button>
                        </div>
                      </>
                    )}

                    {/* Hidden file input */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
                      style={{ display: 'none' }}
                      onChange={(e) => { if (e.target.files[0]) handlePhotoUpload(e.target.files[0]); e.target.value = ''; }}
                    />

                  </div>

                  <div className="stg-profile-fields">
                    <div className="stg-input-row">
                      <div className="stg-input-group">
                        <label className="stg-input-label">Full Name</label>
                        <div className="stg-input-row" style={{ marginBottom: 0 }}>
                          <div className="stg-input-group">
                            <input
                              className="stg-input"
                              type="text"
                              value={editFirstName}
                              onChange={(e) => setEditFirstName(e.target.value)}
                              placeholder="First name"
                            />
                          </div>
                          <div className="stg-input-group">
                            <input
                              className="stg-input"
                              type="text"
                              value={editLastName}
                              onChange={(e) => setEditLastName(e.target.value)}
                              placeholder="Last name"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="stg-input-group narrow">
                        <label className="stg-input-label">Nickname (optional)</label>
                        <input
                          className="stg-input"
                          type="text"
                          value={editNickname}
                          onChange={(e) => setEditNickname(e.target.value)}
                          placeholder="Add a nickname..."
                        />
                      </div>
                    </div>

                    <div className="stg-input-row">
                      <div className="stg-input-group">
                        <label className="stg-input-label">Email Address</label>
                        <div className="stg-input-with-badge">
                          <input
                            className="stg-input"
                            type="email"
                            value={profile?.email || ''}
                            readOnly
                          />
                          {profile?.email_verified && (
                            <div className="stg-verified-badge">
                              <Check size={10} />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="stg-input-group narrow">
                        <label className="stg-input-label">Phone (optional)</label>
                        <input
                          className="stg-input"
                          type="tel"
                          value={editPhone}
                          onChange={(e) => setEditPhone(e.target.value)}
                          placeholder="Add phone..."
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom Row: DOB, Location, Member Since, Update Button */}
                <div className="stg-profile-bottom">
                  <div className="stg-input-group">
                    <label className="stg-input-label">
                      <Calendar size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      Date of Birth
                    </label>
                    <input
                      className="stg-input"
                      type="date"
                      value={editDob}
                      onChange={(e) => setEditDob(e.target.value)}
                    />
                  </div>
                  <div className="stg-input-group">
                    <label className="stg-input-label">
                      <MapPin size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      Location
                    </label>
                    <input
                      className="stg-input"
                      type="text"
                      value={editLocation}
                      onChange={(e) => setEditLocation(e.target.value)}
                      placeholder="City, State"
                    />
                  </div>
                  <div className="stg-input-group">
                    <label className="stg-input-label">Member Since</label>
                    <input
                      className="stg-input-readonly"
                      type="text"
                      value={profile?.date_joined ? new Date(profile.date_joined).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
                      readOnly
                    />
                  </div>
                  <button
                    className="stg-btn stg-btn-primary"
                    onClick={handleUpdateProfile}
                    disabled={profileSaving}
                    style={{ whiteSpace: 'nowrap', alignSelf: 'flex-end', marginBottom: 0 }}
                  >
                    {profileSaving ? 'Saving...' : 'Update Profile'}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Password + Member Since */}
          <div className="stg-card" style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: '#F5F3EE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Lock size={18} color="#828A76" />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#2C2F26' }}>Password</div>
                  <div style={{ fontSize: 13, color: '#9AA088' }}>
                    {profile?.password_changed_at
                      ? `Last changed ${new Date(profile.password_changed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                      : 'Never changed'}
                  </div>
                </div>
              </div>
              <button onClick={() => navigate('/settings/change-password')} style={{
                background: '#fff', border: '1.5px solid #E4E1D2', color: '#556B2F',
                fontWeight: 700, fontSize: 14, padding: '8px 18px', borderRadius: 999,
                cursor: 'pointer', fontFamily: "'Source Sans 3', sans-serif",
              }}>
                Change password
              </button>
            </div>
            {profile?.date_joined && (
              <div style={{ borderTop: '1px solid #F0EEE3', marginTop: 14, paddingTop: 12, fontSize: 13, color: '#9AA088' }}>
                Member since {new Date(profile.date_joined).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </div>
            )}
          </div>
        </div>

        {/* ═══ MY FAMILIES ═══ */}
        <div className="stg-section" id="my-families">
          <div className="stg-section-header">
            <Home size={20} />
            <h2>
              <span className="stg-new-badge">NEW</span>
              My Families
            </h2>
          </div>
          <p className="stg-section-sub">
            Manage your family memberships and roles.
          </p>

          <div className="stg-card">
            {familiesLoading ? (
              <div className="stg-loading">
                <div className="stg-loading-spinner" />
                <span>Loading families...</span>
              </div>
            ) : (
              <>
                {/* Family Cards */}
                <div className="stg-family-list">
                  {families.map((family, index) => (
                    <div
                      key={family.id}
                      className={`stg-family-card ${family.is_primary ? 'primary' : ''}`}
                    >
                      <div
                        className="stg-family-avatar"
                        style={{ background: familyColors[index % familyColors.length] }}
                      >
                        {family.initial || '?'}
                      </div>
                      <div className="stg-family-info">
                        <div className="stg-family-name-row">
                          <span className="stg-family-name">{family.name}</span>
                          {family.is_primary && (
                            <span className="stg-family-badge">Primary</span>
                          )}
                        </div>
                        <div className="stg-family-role">
                          Your role: <strong>{family.role === 'admin' ? 'Family Admin' : family.role}</strong>
                        </div>
                        <div className="stg-family-meta">
                          {family.member_count} member{family.member_count !== 1 ? 's' : ''}
                          {family.joined_at && ` \u00B7 Joined ${new Date(family.joined_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                        </div>
                      </div>
                      <div className="stg-family-actions">
                        {family.is_primary ? (
                          <>
                            <button
                              className="stg-btn-sm primary"
                              onClick={() => navigate('/family-hub')}
                            >
                              Manage <ChevronRight size={12} />
                            </button>
                            <button className="stg-btn-sm">Settings</button>
                          </>
                        ) : (
                          <>
                            <button
                              className="stg-btn-sm"
                              onClick={() => navigate('/family-hub')}
                            >
                              View <ChevronRight size={12} />
                            </button>
                            <button
                              className="stg-btn-orange-outline"
                              onClick={() => handleSetPrimary(family.id)}
                            >
                              Set Primary
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}

                  {families.length === 0 && (
                    <div className="stg-loading" style={{ color: '#999' }}>
                      No families found. Create or join one below.
                    </div>
                  )}
                </div>

                {/* Create / Join Family */}
                <div className="stg-family-action-row">
                  <button className="stg-dashed-btn" onClick={handleCreateFamily}>
                    <span className="stg-dashed-btn-icon">+</span>
                    <span className="stg-dashed-btn-label">Create New Family</span>
                  </button>
                  <button className="stg-dashed-btn secondary" onClick={handleJoinFamily}>
                    <span className="stg-dashed-btn-icon"><ExternalLink size={18} /></span>
                    <span className="stg-dashed-btn-label">Join Family with Invite Code</span>
                  </button>
                </div>

                {/* Pending Invitations */}
                {pendingInvitations.length > 0 && (
                  <div className="stg-invitation-banner">
                    <span>
                      <Mail size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                      You have {pendingInvitations.length} pending family invitation{pendingInvitations.length !== 1 ? 's' : ''}
                    </span>
                    <a className="stg-invitation-banner-action" onClick={() => navigate('/family-hub')}>
                      View <ChevronRight size={12} style={{ verticalAlign: 'middle' }} />
                    </a>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ═══ 1. PRIVACY ═══ */}
        <div className="stg-section" id="privacy">
          <div className="stg-section-header">
            <Lock size={20} />
            <h2>Privacy</h2>
          </div>
          <p className="stg-section-sub">
            Control who can see your stories and interact with your content.
          </p>

          {/* Who can see my stories? */}
          <p className="stg-field-label">
            <Eye size={16} />
            Who can see my stories?
          </p>

          <div className="stg-card" style={{ marginBottom: 20 }}>
            <div className="stg-radio-group">
              {/* Family Only */}
              <div
                className={`stg-radio-option ${storyVisibility === 'family' ? 'selected' : ''}`}
                onClick={() => {
                  setStoryVisibility('family');
                  updateUserProfile({ default_story_visibility: 'family' }).catch(() => {});
                }}
              >
                <div className="stg-radio-dot">
                  <div className="stg-radio-dot-inner" />
                </div>
                <div className="stg-radio-icon" style={{ background: '#F1F8E9', color: '#556B2F' }}>
                  <Users size={18} />
                </div>
                <div>
                  <div className="stg-radio-label">Family Only</div>
                  <div className="stg-radio-sub">Only invited family members can view your stories</div>
                </div>
              </div>

              {/* Private */}
              <div
                className={`stg-radio-option ${storyVisibility === 'private' ? 'selected' : ''}`}
                onClick={() => {
                  setStoryVisibility('private');
                  updateUserProfile({ default_story_visibility: 'private' }).catch(() => {});
                }}
              >
                <div className="stg-radio-dot">
                  <div className="stg-radio-dot-inner" />
                </div>
                <div className="stg-radio-icon" style={{ background: '#FFF3E0', color: '#E65100' }}>
                  <Lock size={18} />
                </div>
                <div>
                  <div className="stg-radio-label">Private</div>
                  <div className="stg-radio-sub">Only you can see your stories</div>
                </div>
              </div>
            </div>
            <p style={{ fontSize: 13, color: '#9AA088', marginTop: 10, fontStyle: 'italic' }}>
              This sets the default for new stories. Your existing stories are not affected.
            </p>
          </div>

          {/* Profile Visibility */}
          <p className="stg-field-label">
            <User size={16} />
            Profile visibility
          </p>

          <div className="stg-card">
            <div className="stg-visibility-row">
              <div
                className={`stg-visibility-option ${profileVisibility === 'family' ? 'selected' : ''}`}
                onClick={() => setProfileVisibility('family')}
              >
                <div className="stg-visibility-icon" style={{ background: '#F1F8E9', color: '#556B2F' }}>
                  <Users size={16} />
                </div>
                <div>
                  <div className="stg-visibility-label">Family</div>
                  <div className="stg-visibility-sub">Visible to family</div>
                </div>
              </div>
              <div
                className={`stg-visibility-option ${profileVisibility === 'private' ? 'selected' : ''}`}
                onClick={() => setProfileVisibility('private')}
              >
                <div className="stg-visibility-icon" style={{ background: '#F5F5F5', color: '#999' }}>
                  <Lock size={16} />
                </div>
                <div>
                  <div className="stg-visibility-label">Private</div>
                  <div className="stg-visibility-sub">Only you</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ═══ STEWARDSHIP ═══ */}
        <div className="stg-section" id="stewardship">
          <div className="stg-section-header">
            <Shield size={20} />
            <h2>Stewardship</h2>
          </div>
          <p className="stg-section-sub">
            Ensure your letters reach their recipients even if you're no longer able to deliver them yourself.
          </p>

          <div className="stg-card" style={{ marginBottom: 16 }}>
            {/* Info text */}
            <div className="stg-stewardship-info">
              <p>
                As a steward is chosen, your letters reach their recipients even if the sender
                can no longer deliver them personally. Stewards can manage delivery but{' '}
                <strong>cannot read or edit</strong> your letters.
              </p>
            </div>

            {/* Enable stewardship toggle */}
            <div className="stg-toggle-group">
              <ToggleRow
                title="Enable stewardship"
                subtitle="Allow a trusted family member to manage delivery of your capsules"
                enabled={enableStewardship}
                onChange={handleStewardshipToggle}
              />
              {!enableStewardship && !stewardshipError && (
                <div className="stg-steward-role" role="status" style={{ padding: '0 20px 12px' }}>
                  New letters won&apos;t get a steward by default. Letters that already have one keep it.
                </div>
              )}
              {stewardshipError && (
                <div className="stg-steward-role" role="alert" style={{ padding: '0 20px 12px', color: '#B3261E' }}>
                  {stewardshipError}
                </div>
              )}
            </div>

            {/* Stewards currently named on this person's letters */}
            <div className="stg-sub-header">
              <Users size={16} />
              <span className="stg-sub-header-text">Your Stewards</span>
            </div>

            {loadingStewardship ? (
              <div className="stg-loading">
                <div className="stg-loading-spinner" />
                <span>Loading stewardship data...</span>
              </div>
            ) : stewardship ? (
              <>
                {[
                  ...stewardship.designations.map((d) => ({ ...d, designated: true })),
                  ...stewardship.other_stewards.map((s) => ({ ...s, designated: false })),
                ].map((steward, index) => {
                  const initials = `${(steward.first_name?.[0] || '').toUpperCase()}${(steward.last_name?.[0] || '').toUpperCase()}`;
                  const colors = ['#556B2F', '#E65100', '#2E4057', '#6B3FA0', '#C2185B', '#00695C'];
                  const relationship = stewardRelationships[steward.person_id];
                  const count = steward.letter_count;
                  const isBackup = steward.role === 'BACKUP';
                  const countText = (n) => `${n} ${n === 1 ? 'letter' : 'letters'}`;
                  let roleText;
                  if (steward.designated) {
                    roleText = `${isBackup ? 'Backup' : 'Steward'} for ${countText(count)}`;
                  } else {
                    const parts = [];
                    if (count > 0) parts.push(`Steward for ${countText(count)}`);
                    if (steward.backup_letter_count > 0) parts.push(`Backup for ${countText(steward.backup_letter_count)}`);
                    roleText = parts.join(' \u00b7 ');
                  }
                  return (
                    <div className="stg-steward-row" key={steward.person_id}>
                      <div className="stg-steward-avatar" style={{ background: colors[index % colors.length] }}>
                        {initials || '?'}
                      </div>
                      <div className="stg-steward-info">
                        <div className="stg-steward-name">
                          {steward.first_name} {steward.last_name}{' '}
                          {steward.designated && (
                            <span className={`stg-steward-badge ${isBackup ? 'stg-steward-badge-secondary' : 'stg-steward-badge-primary'}`}>
                              {isBackup ? 'Backup' : 'Primary'}
                            </span>
                          )}
                        </div>
                        <div className="stg-steward-role">
                          {relationship ? `${relationship} \u00b7 ` : ''}{roleText}
                          {!steward.designated && ' \u00b7 chosen on individual letters'}
                        </div>
                      </div>
                      {steward.designated && (
                        <div className="stg-steward-actions">
                          <button
                            className="stg-btn-sm"
                            onClick={() => { setStewardNotice(''); setStewardDialog({ mode: 'change', designation: steward }); }}
                          >
                            Change
                          </button>
                          <button
                            className="stg-btn-sm"
                            onClick={() => { setStewardNotice(''); setStewardDialog({ mode: 'remove', designation: steward }); }}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}

                {!hasPrimary && (
                  <div className="stg-loading" style={{ color: '#999' }}>
                    No default steward yet. New letters will start with no steward.
                  </div>
                )}

                {!hasPrimary && (
                  <div className="stg-cta-row">
                    <button
                      className="stg-btn stg-btn-primary"
                      onClick={() => { setStewardNotice(''); setStewardDialog({ mode: 'add', role: 'PRIMARY' }); }}
                    >
                      <Plus size={15} />
                      Choose a Primary steward
                    </button>
                  </div>
                )}

                {hasPrimary && !hasBackup && (
                  <div className="stg-cta-row">
                    <button
                      className="stg-btn stg-btn-outline"
                      onClick={() => { setStewardNotice(''); setStewardDialog({ mode: 'add', role: 'BACKUP' }); }}
                    >
                      <Plus size={15} />
                      Add a Backup steward
                    </button>
                  </div>
                )}

                {stewardNotice && (
                  <div className="stg-steward-role" role="status" style={{ padding: '0 20px 12px' }}>
                    {stewardNotice}
                  </div>
                )}
              </>
            ) : null}

            {stewardDialog && (
              <StewardDialog
                mode={stewardDialog.mode}
                role={stewardDialog.role}
                designation={stewardDialog.designation}
                stewardship={stewardship}
                onClose={() => setStewardDialog(null)}
                onDone={handleStewardDone}
              />
            )}

            {!loadingStewardship && stewardship?.letters_without_steward > 0 && (
              <div className="stg-steward-role" style={{ padding: '12px 20px' }}>
                {stewardship.letters_without_steward} of your waiting {stewardship.letters_without_steward === 1 ? 'letter has' : 'letters have'} no steward.
              </div>
            )}

            {!loadingStewardship && stewardship?.summary?.total_stewarding > 0 && (
              <div className="stg-cta-row">
                <button className="stg-btn stg-btn-outline" onClick={() => navigate('/stewarded-letters')}>
                  Letters entrusted to you ({stewardship.summary.total_stewarding})
                  <ChevronRight size={15} />
                </button>
              </div>
            )}
          </div>

        </div>

        {/* ═══ CLOSE ACCOUNT ═══ */}
        <div className="stg-section" id="close-account" style={{ marginTop: 32 }}>
          <div style={{
            background: '#FFFBFA', border: '1.5px solid #F0B4AC', borderRadius: 14,
            padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#8B1A1A', marginBottom: 4 }}>Close your account</div>
              <div style={{ fontSize: 14, color: '#9A5A5A' }}>Permanently delete your personal data. Your stories stay with your family.</div>
            </div>
            <button onClick={() => navigate('/settings/account/delete')} style={{
              background: '#fff', border: '1.5px solid #F0B4AC', color: '#C0392B',
              fontWeight: 700, fontSize: 14, padding: '10px 20px', borderRadius: 999,
              cursor: 'pointer', whiteSpace: 'nowrap', minHeight: 44,
            }}>
              Close account
            </button>
          </div>
        </div>

        {/* Spacer for save bar */}
        <div style={{ height: 60 }} />
      </div>

      {/* Sticky Save Bar */}
      <div className="stg-save-bar">
        <span className="stg-save-bar-note">Changes are saved automatically</span>
        <button className="stg-btn stg-btn-outline">
          <RotateCcw size={15} />
          Reset to Defaults
        </button>
        <button className="stg-btn stg-btn-primary">
          <Save size={15} />
          Save Changes
        </button>
      </div>
    </HubLayout>
    {showSelfieModal && (
      <SelfieModal
        isOpen={showSelfieModal}
        onClose={() => setShowSelfieModal(false)}
        onCapture={(file) => handlePhotoUpload(file)}
      />
    )}
    </>
  );
}

export default SettingsPage;
